import React, { useMemo, useEffect } from 'react';
import { useListState } from '@react-stately/list';
import { Item, Section } from '@react-stately/collections';
import { useFilter } from '@react-aria/i18n';
import { compact } from '../../utils/index.js';
import { addVariantToItems, filterNodes, findItemsByKeys, isSection, sortItemsAlphanumerically, sortItemsBySelection, } from './utils.js';
import { SelectListItem } from './select-list-item.component.js';
export const useSelectListState = (props, state) => {
    const { selectionMode, selectNoneLabel, defaultItems, sortSelectedItems, disableInternalSearchFilter, selectedItems, selectedItem, sortMode, } = props;
    const { flattenedItems, cachedSelectedItems, setCachedSelectedItems, uncontrolledFilterValue, cachedSelectedIds, buttonState, } = state;
    const { contains } = useFilter({ sensitivity: 'base' });
    const isControlled = ['selectedItems', 'selectedItem'].some((prop) => Object.prototype.hasOwnProperty.call(props, prop));
    useEffect(() => {
        if (!isControlled)
            return;
        setCachedSelectedItems(compact(selectionMode === 'multiple' ? selectedItems : [selectedItem]));
    }, [selectedItems, selectedItem]);
    const items = useMemo(() => {
        const noneItem = selectNoneLabel
            ? [
                {
                    id: '__none__',
                    label: selectNoneLabel,
                    variant: 'none',
                },
            ]
            : [];
        const itemsWithVariant = [
            ...noneItem,
            ...addVariantToItems(defaultItems, selectionMode),
        ];
        const selectedItemsWithVariant = addVariantToItems(cachedSelectedItems, selectionMode);
        if (sortSelectedItems) {
            return sortItemsBySelection(itemsWithVariant, selectedItemsWithVariant);
        }
        return itemsWithVariant;
    }, [defaultItems, selectionMode, selectNoneLabel, cachedSelectedItems]);
    const handleSelectionChange = (keys) => {
        if (keys === 'all') {
            return props.onSelectionChange?.(flattenedItems);
        }
        if (selectionMode === 'multiple') {
            const _selectedItems = findItemsByKeys(flattenedItems, cachedSelectedItems)(keys);
            if (sortMode === 'alphanumeric') {
                sortItemsAlphanumerically(_selectedItems);
            }
            return props.onSelectionChange?.(_selectedItems);
        }
        buttonState.close();
        const [key] = Array.from(keys);
        return props.onSelectionChange?.(flattenedItems.find((item) => item.id === key));
    };
    const listState = useListState({
        items,
        children: (item) => {
            if (!isSection(item)) {
                return (React.createElement(Item, { textValue: item.label },
                    React.createElement(SelectListItem, { item: item })));
            }
            return (React.createElement(Section, { items: item.items, title: item.label, "aria-label": item.label }, (node) => (React.createElement(Item, { key: node.id, textValue: node.label },
                React.createElement(SelectListItem, { item: node })))));
        },
        ...(!isControlled && {
            defaultSelectedKeys: cachedSelectedIds,
        }),
        ...(isControlled && {
            selectedKeys: cachedSelectedIds,
        }),
        selectionMode,
        onSelectionChange: handleSelectionChange,
        filter: disableInternalSearchFilter
            ? undefined
            : filterNodes((value) => contains(value, uncontrolledFilterValue)),
    });
    return listState;
};
//# sourceMappingURL=use-select-list-state.js.map